import React, { useEffect, createElement } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Clock } from 'lucide-react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { getPostMeta, getPostBody, blogPosts } from './blog/posts';
export function BlogPost() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const meta = slug ? getPostMeta(slug) : undefined;
  const relatedPosts = blogPosts.filter(p => p.slug !== slug).slice(0, 2);
  useEffect(() => {
    window.scrollTo(0, 0);
    if (meta) {
      document.title = `${meta.title} | IntelliEats Blog`;
    }
    // Google AdSense - inject into <head>
    if (!document.querySelector('script[src*="adsbygoogle"]')) {
      const adsScript = document.createElement('script');
      adsScript.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-3041826185319356';
      adsScript.async = true;
      adsScript.setAttribute('crossorigin', 'anonymous');
      document.head.appendChild(adsScript);
    }
  }, [meta]);
  if (!meta) {
    return <Navigate to="/blog" replace data-id="element-408" />;
  }
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="pt-32 pb-24 bg-white min-h-screen" data-id="element-409">
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-410">
        {/* Header */}
        <header className="mb-12 text-center" data-id="element-411">
          <Link to="/blog" className="inline-flex items-center text-sm font-medium text-brand-600 hover:text-brand-700 mb-8 transition-colors" data-id="element-412">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-413" />
            Back to Blog
          </Link>
          <h1 className="text-4xl sm:text-5xl font-black text-slate-900 tracking-tight leading-tight mb-6" data-id="element-414">
            {meta.title}
          </h1>
          <div className="flex items-center justify-center text-sm text-slate-500 space-x-4" data-id="element-415">
            <span data-id="element-416">By IntelliEats Team</span>
            <span data-id="element-417">•</span>
            <span className="flex items-center" data-id="element-418">
              <Clock className="w-4 h-4 mr-1" data-id="element-419" />
              {meta.readTime}
            </span>
          </div>
        </header>

        {/* Body */}
        {getPostBody(meta.slug)}

        {/* Medical Disclaimer */}
        <div className="mt-16 bg-slate-50 border border-slate-200 rounded-2xl p-6" data-id="element-420">
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-2" data-id="element-421">
            Medical Disclaimer
          </h2>
          <p className="text-sm text-slate-500 leading-relaxed m-0" data-id="element-422">
            The information in this article is provided for educational purposes
            only and is not intended as medical, nutritional, or dietary advice.
            Ingredients approved by regulatory authorities are considered safe
            when used within established limits. Always consult a qualified
            healthcare professional or registered dietitian for guidance
            tailored to your personal health needs. See our{' '}
            <Link to="/editorial-policy" className="text-brand-600 hover:text-brand-700 font-medium" data-id="element-423">
              Editorial Policy
            </Link>{' '}
            to learn how we research and review our content.
          </p>
        </div>

        {/* Related Articles */}
        {relatedPosts.length > 0 && <div className="mt-16 pt-12 border-t border-slate-200" data-id="element-424">
            <h2 className="text-2xl font-black text-slate-900 mb-8 text-center" data-id="element-425">
              Keep Reading
            </h2>
            <div className="grid sm:grid-cols-2 gap-6" data-id="element-426">
              {relatedPosts.map(post => <Link key={post.slug} to={`/blog/${post.slug}`} className="group block bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden text-left" data-id="element-427">
                  <div className="h-40 overflow-hidden" data-id="element-428">
                    <img src={post.image} alt={post.imageAlt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-429" />
                  </div>
                  <div className="p-6" data-id="element-430">
                    <span className="px-2.5 py-1 rounded-full bg-brand-50 text-brand-700 text-xs font-bold tracking-wider uppercase" data-id="element-431">
                      {post.category}
                    </span>
                    <h3 className="text-lg font-bold text-slate-900 mt-3 mb-3 leading-snug group-hover:text-brand-700 transition-colors line-clamp-2" data-id="element-432">
                      {post.title}
                    </h3>
                    <span className="inline-flex items-center text-sm font-semibold text-brand-600 group-hover:text-brand-700" data-id="element-433">
                      Read Article
                      <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" data-id="element-434" />
                    </span>
                  </div>
                </Link>)}
            </div>
          </div>}
      </article>
    </motion.div>;
}